// One-shot /settings read for web-only action code paths.
//
// The portal already holds /settings in Redux (fetchSettings at boot), yet
// several actions re-read the node from RTDB on every call — addToWallet did
// it once per credited driver inside the FinanceAdminControls bulk loops.
// getSettingsOnce() returns the store copy when present, otherwise a 60 s
// module-cached get() with in-flight dedup so a burst of concurrent calls
// costs a single read.
//
// React Native gate: on mobile this is a fresh get(settingsRef) every call —
// byte-identical to the inline read it replaces — so no mobile flow changes.
// Only swap web-only call sites onto this helper.
import { get } from "firebase/database";
import { firebase } from '../config/configureFirebase';
import store from '../store/store';

const SETTINGS_CACHE_TTL_MS = 60 * 1000;
let cached = null;   // { value, at }
let inflight = null; // Promise<value> while a read is in progress

const isReactNative = () =>
  typeof window === 'undefined' ||
  typeof document === 'undefined' ||
  (typeof navigator !== 'undefined' && navigator.product === 'ReactNative');

export const getSettingsOnce = async () => {
  const { settingsRef } = firebase;
  if (isReactNative()) {
    const snapshot = await get(settingsRef);
    return snapshot.val();
  }
  try {
    const fromStore = store.getState().settingsdata.settings;
    if (fromStore) return fromStore;
  } catch (_) { /* store not ready — fall through to the cached read */ }
  if (cached && Date.now() - cached.at < SETTINGS_CACHE_TTL_MS) return cached.value;
  if (!inflight) {
    inflight = get(settingsRef).then(
      (snapshot) => {
        const value = snapshot.val();
        cached = { value: value, at: Date.now() };
        inflight = null;
        return value;
      },
      (error) => {
        inflight = null;
        throw error;
      }
    );
  }
  return inflight;
};
